import React, { Children, cloneElement, forwardRef, isValidElement } from 'react';
import AssetIcon from '../assets/AssetIcon';
import { useIconSettings } from '../assets/IconSettingsProvider';
import { resolveIconSize } from '../assets/icon-sizes';
import { actionIconForLabel, getIconLabel } from '../assets/icon-labels';
import PopUpInfos, { actionText } from './PopUpInfos';
import './icon-action.css';

function findIcon(children) {
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    if (child.type?.iconName || child.type === AssetIcon || child.type === 'svg') return child;
    const nested = findIcon(child.props.children);
    if (nested) return nested;
  }
  return null;
}

const IconAction = forwardRef(function IconAction(
  {
    asChild = false,
    children,
    label,
    info,
    iconKey,
    icon: Icon,
    tone = 'default',
    size = 'action',
    customSize,
    className = '',
    style,
    ...props
  },
  ref,
) {
  const settings = useIconSettings();
  const child = asChild ? Children.only(children) : null;
  const content = child ? child.props.children : children;
  const text =
    label ||
    props['aria-label'] ||
    child?.props['aria-label'] ||
    actionText(content) ||
    getIconLabel(iconKey || Icon?.iconName);
  const existingIcon = !iconKey && !Icon ? findIcon(content) : null;
  const key =
    iconKey ||
    Icon?.iconName ||
    existingIcon?.props.name ||
    existingIcon?.type?.iconName ||
    actionIconForLabel(text);
  const resolvedTone =
    tone !== 'default'
      ? tone
      : key === 'delete' || key === 'actions.delete'
        ? 'danger'
        : key === 'edit' || key === 'actions.edit'
          ? 'edit'
          : 'default';
  const actionProps = {
    ...props,
    ...(ref ? { ref } : {}),
    disabled: props.disabled ?? child?.props.disabled ?? false,
    className: ['wt-icon-action', `wt-icon-action--${resolvedTone}`, child?.props.className, className]
      .filter(Boolean)
      .join(' '),
    style: {
      ...child?.props.style,
      ...style,
      '--wt-action-icon-size': resolveIconSize(size, settings, customSize),
    },
    'data-icon-action': '',
    'aria-label': text,
    title: undefined,
  };
  const icon = Icon ? (
    <Icon className="wt-icon-action__icon" size={size} customSize={customSize} aria-hidden="true" />
  ) : existingIcon ? (
    cloneElement(existingIcon, {
      size,
      customSize,
      className: `wt-icon-action__icon ${existingIcon.props.className || ''}`,
      'aria-hidden': true,
    })
  ) : (
    <AssetIcon className="wt-icon-action__icon" name={key} size={size} customSize={customSize} />
  );
  const body = (
    <>
      {icon}
      <span className="wt-icon-action__label">{actionText(content) || text}</span>
    </>
  );
  const trigger = child ? (
    cloneElement(child, actionProps, body)
  ) : (
    <button type="button" {...actionProps}>
      {body}
    </button>
  );
  return (
    <PopUpInfos label={info || props.title || child?.props.title || text} iconKey={key}>
      {trigger}
    </PopUpInfos>
  );
});
export default IconAction;
